// Registry of the dedicated search pages: every city and property-type
// landing that renders as a preset of the shared search layout. This module
// is the single source of truth for which slugs exist, whether each renders
// or redirects, and its scoping/branding/head configuration.
//
// PURE CommonJS on purpose: next-sitemap.config.js require()s this file at
// build time inside the Docker image's Node 18, where require() of ESM
// throws (and next-sitemap swallows the error). Keep it free of ESM syntax
// and of imports that transitively pull ESM; verify changes with
//   docker run --rm -v "$PWD/src:/app/src:ro" node:18-alpine \
//     node -e "require('/app/src/config/searchPages.js')"
//
// Entry shape per kind:
//   city:  { kind, status, floor, head, company?, branding?, floorMode? }
//          floorMode "filter" (default): the boundary scopes results.
//          floorMode "anchor": boundary is painted + framed but results
//          scope by the page's company — requires a city-precise company.
//   type:  { kind, status, defaults: { type }, typeCategory, siblings, head }
//   any:   status "redirect" needs nothing else — the page 308s to /Buscar.
// A slug with no entry (and no page file) is a 404, exactly like today.
//
// `floor` is the page's default boundary (municipio outline(s) from INEGI,
// built by scripts/floors/build-floors.js). `company` omitted = the
// all-companies default. Before adding a NEW slug here, check the CDN
// path-behavior config first — this repo does not own the whole top-level
// namespace (see the routing survey under scripts/audit/search-pages/).

const searchPages = {
    // ---- City pages -------------------------------------------------------
    zmg: {
        kind: "city",
        status: "active",
        floor: require("./floors/zmg.json"),
        branding: {
            // Generic brand mark (no city name in the artwork) — the label
            // supplies the page identity next to it.
            logoSrc: "https://media.grupoguia.mx/assets/home-page/zmg/Logo_guia.png",
            alt: "Guía ZMG",
            label: "ZMG",
        },
        head: { title: "Guadalajara y ZMG | Grupo Guía", canonicalPath: "/zmg" },
    },
    cancun: {
        kind: "city",
        status: "active",
        floor: require("./floors/cancun.json"),
        branding: {
            logoSrc: "https://media.grupoguia.mx/assets/home-page/cancun/guiacancun.webp",
            alt: "Guía Cancún",
        },
        head: { title: "Cancún | Grupo Guía", canonicalPath: "/cancun" },
    },
    cdmx: {
        kind: "city",
        status: "active",
        floor: require("./floors/cdmx.json"),
        // Anchor mode: the CDMX team's portfolio spans well beyond the 16
        // alcaldías (Estado de México, Hidalgo, Morelos…), and unlike the
        // umbrella company, GUIACDMX maps 1:1 to this market — so the page
        // scopes by company and keeps the boundary as the visual anchor.
        // The map still shows every property where it truly is; zooming out
        // reveals the ones outside the boundary. Do NOT copy this mode to
        // pages whose company is the GUIA umbrella (it would list the whole
        // multi-market inventory under one city's label).
        floorMode: "anchor",
        company: ["GUIACDMX"],
        branding: {
            logoSrc: "https://media.grupoguia.mx/assets/home-page/cdmx/logo-cdmx.svg",
            alt: "Guía CDMX",
        },
        head: { title: "CDMX | Grupo Guía", canonicalPath: "/cdmx" },
    },
    queretaro: {
        kind: "city",
        status: "active",
        floor: require("./floors/queretaro.json"),
        branding: {
            logoSrc: "https://media.grupoguia.mx/assets/home-page/queretaro/logo_queretaro.webp",
            alt: "Guía Querétaro",
        },
        head: { title: "Querétaro | Grupo Guía", canonicalPath: "/queretaro" },
    },
    tapalpa: {
        kind: "city",
        status: "active",
        floor: require("./floors/tapalpa.json"),
        branding: {
            logoSrc: "https://media.grupoguia.mx/assets/home-page/tapalpa/logo-guia-tapalpa.webp",
            alt: "Guía Tapalpa",
        },
        head: { title: "Tapalpa | Grupo Guía", canonicalPath: "/tapalpa" },
    },
    // Discontinued markets keep their URLs alive as permanent redirects so
    // inbound links and search equity land on the live search instead of a
    // dead storefront. Reactivating one = flip to "active" + add floor/head
    // (the boundary JSONs for these three are already built).
    leon: { kind: "city", status: "redirect" },
    loscabos: { kind: "city", status: "redirect" },
    puertovallarta: { kind: "city", status: "redirect" },

    // ---- Property-type pages ---------------------------------------------
    // Retired 2026-09: Search Console (3-month window) showed essentially no
    // search equity on these paths — /departamentos had 2 clicks/50
    // impressions and /casas & /terrenos didn't register at all, while
    // /Buscar (which supersedes them functionally via the tipo= filter) drew
    // 42/2,455. Redirecting preserves what little inbound traffic exists.
    // Reactivating one = flip to "active" and restore its full entry — the
    // type value-groups, sibling map, and head — from this file's git
    // history. The sibling-navigation logic in usePropertyTypeDraft stays
    // in the code, config-gated and dormant.
    casas: { kind: "type", status: "redirect" },
    departamentos: { kind: "type", status: "redirect" },
    terrenos: { kind: "type", status: "redirect" },
};

module.exports = { searchPages };
